<script>
import { computed, ref, watch } from "vue"
import { useStore } from "@/utils/vuex-utils"
import Modal from "../Modal.vue"
import LocationSelector from "../utils/LocationSelector.vue"

export default {
    name: "WeatherLocationModal",
    components: {
        Modal,
        LocationSelector,
    },
    setup() {
        const store = useStore()

        const showLocationModal = computed(() => store.getters["weather/showLocationModal"])
        const coordinates = computed(() => store.getters["weather/coordinates"])

        const tempCoordinates = ref({ latitude: null, longitude: null })

        watch(showLocationModal, (newValue) => {
            if (newValue) {
                if (coordinates.value) {
                    tempCoordinates.value = {
                        latitude: coordinates.value.latitude,
                        longitude: coordinates.value.longitude,
                    }
                }

                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            tempCoordinates.value = {
                                latitude: position.coords.latitude,
                                longitude: position.coords.longitude,
                            }
                        },
                        () => {},
                        {
                            enableHighAccuracy: true,
                            timeout: 10000,
                            maximumAge: 60000,
                        },
                    )
                }
            }
        })

        const handleCloseModal = () => {
            store.commit("weather/SET_SHOW_LOCATION_MODAL", false)
        }

        const handleSaveLocation = async () => {
            const weatherData = store.getters["weather/data"]

            if (!weatherData) {
                const locationId = store.getters["weather/locationId"]
                if (locationId) {
                    await store.dispatch("weather/fetchLocation", locationId)
                } else {
                    return
                }
            }

            store.dispatch("weather/saveLocationCoordinates", tempCoordinates.value)
        }

        return {
            showLocationModal,
            tempCoordinates,
            handleCloseModal,
            handleSaveLocation,
        }
    },
}
</script>

<template>
    <Modal
        v-if="showLocationModal"
        :title="$t('weather.setLocation')"
        @close="handleCloseModal"
        class="prevent-ptr"
    >
        <template #body>
            <div class="location-modal-content">
                <LocationSelector v-model="tempCoordinates" />
                <div class="modal-actions">
                    <button class="btn btn-secondary" @click="handleCloseModal">
                        {{ $t("Cancel") }}
                    </button>
                    <button class="btn btn-primary" @click="handleSaveLocation">
                        {{ $t("Save") }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<style lang="scss" scoped>
.location-modal-content {
    padding: 1rem;
    min-height: 400px;
    width: 100%;

    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
        margin-top: 1rem;
        padding-top: 1rem;
        border-top: 1px solid var(--theme-shade);

        .btn {
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 0.5rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;

            &.btn-secondary {
                background: var(--theme-shade);
                color: var(--theme-secondary);

                &:hover {
                    background: color-mix(in srgb, var(--theme-shade) 80%, black);
                }
            }

            &.btn-primary {
                background: var(--theme-primary);
                color: var(--theme-background);

                &:hover {
                    background: var(--theme-secondary);
                }
            }
        }
    }
}
</style>
